const translations = {
  en: {
    // -------------------------------------------------------
    // Brand
    // -------------------------------------------------------
    brand: {
      name: "create-song",
      tagline: "Your music, your way",
    },

    // -------------------------------------------------------
    // Navbar
    // -------------------------------------------------------
    nav: {
      home: "Home",
      createSong: "Create Song",
      mySongs: "My Songs",
      videoCreator: "Video Creator",
      login: "Log In",
      register: "Sign Up",
      logout: "Log Out",
      profile: "Profile",
      language: "Language",
    },

    // -------------------------------------------------------
    // Landing page
    // -------------------------------------------------------
    landing: {
      hero: {
        title: "Create Your Own Song in Minutes",
        subtitle:
          "Write lyrics, choose a voice, pick a style — and let AI turn it into a real song.",
        cta: "Start Creating",
        ctaSecondary: "See Examples",
      },
      howItWorks: {
        heading: "How It Works",
        stepLabel: "Step",
        step1: {
          title: "Write Your Lyrics",
          description:
            "Type your own lyrics, let AI generate them from a prompt, or answer a few guided questions.",
        },
        step2: {
          title: "Choose Voice & Style",
          description:
            "Pick an AI voice or record your own. Select genre, mood, tempo, and energy level.",
        },
        step3: {
          title: "Generate & Share",
          description:
            "Hit generate, sit back, and in moments your song is ready to play, download, and share.",
        },
      },
      examples: {
        heading: "Example Songs",
        listenBtn: "Listen",
        madeWith: "Made with SongCraft",
      },
      cta: {
        title: "Ready to Make Your Song?",
        subtitle: "Start for free — 3 songs per month, no credit card required.",
        button: "Get Started Free",
      },
      footer: "© 2025 create-song. Powered by AI.",
    },

    // -------------------------------------------------------
    // Auth
    // -------------------------------------------------------
    auth: {
      login: {
        title: "Welcome Back",
        subtitle: "Log in to continue creating music",
        emailLabel: "Email",
        emailPlaceholder: "you@example.com",
        passwordLabel: "Password",
        passwordPlaceholder: "Enter your password",
        submitBtn: "Log In",
        forgotPassword: "Forgot password?",
        noAccount: "Don't have an account?",
        signUpLink: "Sign up",
        orDivider: "or",
        googleBtn: "Continue with Google",
        errorInvalid: "Invalid email or password.",
        errorGeneric: "Something went wrong. Please try again.",
      },
      register: {
        title: "Create Your Account",
        subtitle: "Start making songs in minutes",
        nameLabel: "Full Name",
        namePlaceholder: "Your name",
        emailLabel: "Email",
        emailPlaceholder: "you@example.com",
        passwordLabel: "Password",
        passwordPlaceholder: "Create a password",
        confirmPasswordLabel: "Confirm Password",
        confirmPasswordPlaceholder: "Re-enter your password",
        submitBtn: "Sign Up",
        hasAccount: "Already have an account?",
        loginLink: "Log in",
        orDivider: "or",
        googleBtn: "Sign up with Google",
        errorPasswordMismatch: "Passwords do not match.",
        errorEmailTaken: "An account with this email already exists.",
        errorGeneric: "Something went wrong. Please try again.",
        termsNotice: "By signing up you agree to our",
        termsLink: "Terms of Service",
        andWord: "and",
        privacyLink: "Privacy Policy",
      },
      forgotPassword: {
        title: "Reset Password",
        subtitle: "We'll send a reset link to your email",
        emailLabel: "Email",
        emailPlaceholder: "you@example.com",
        submitBtn: "Send Reset Link",
        backToLogin: "Back to login",
        successMessage: "Check your inbox for a reset link.",
      },
    },

    // -------------------------------------------------------
    // Song Creator  (4 steps)
    // -------------------------------------------------------
    songCreator: {
      pageTitle: "Create a Song",
      stepper: {
        lyrics: "Lyrics",
        voice: "Voice",
        style: "Style",
        review: "Review",
      },
      nav: {
        back: "Back",
        next: "Next",
        generate: "Generate Song",
      },

      // Step 1 — Lyrics
      lyrics: {
        heading: "Write Your Lyrics",
        tabs: {
          write: "Write Your Own",
          ai: "AI from Prompt",
          guided: "Guided Q&A",
        },
        write: {
          placeholder: "Type or paste your lyrics here...",
          charCount: "{{count}} characters",
        },
        ai: {
          label: "Describe the song you want",
          placeholder:
            "e.g. A cheerful pop song about a summer road trip with friends",
          generateBtn: "Generate Lyrics",
          regenerateBtn: "Try Again",
          loading: "Writing lyrics...",
          acceptBtn: "Use These Lyrics",
          editBtn: "Edit Before Using",
        },
        guided: {
          q1: "What is the song about?",
          q1Placeholder: "e.g. Moving to a new city",
          q2: "What mood or feeling should it have?",
          q2Placeholder: "e.g. Hopeful but a little nervous",
          q3: "Any specific words or phrases to include?",
          q3Placeholder: "e.g. new beginnings, open road",
          q4: "Who is the song for?",
          q4Placeholder: "e.g. Myself, a friend, my partner",
          generateBtn: "Create Lyrics from Answers",
          loading: "Crafting your lyrics...",
        },
        songTitle: {
          label: "Song Title",
          placeholder: "Give your song a name",
        },
        validation: {
          tooShort: "Lyrics are too short. Write at least a few lines.",
          titleRequired: "Please enter a song title.",
        },
      },

      // Step 2 — Voice
      voice: {
        heading: "Choose a Voice",
        tabs: {
          ai: "AI Voice",
          record: "Record Your Voice",
          upload: "Upload Audio",
        },
        ai: {
          label: "Select an AI voice",
          previewBtn: "Preview",
          male: "Male",
          female: "Female",
          neutral: "Neutral",
        },
        record: {
          instructions: "Tap the button and sing or speak your lyrics.",
          startBtn: "Start Recording",
          stopBtn: "Stop Recording",
          recordingStatus: "Recording...",
          playbackBtn: "Play Back",
          retryBtn: "Record Again",
          useBtn: "Use This Recording",
          permissionError:
            "Microphone access is required. Please allow it in your browser settings.",
        },
        upload: {
          instructions: "Upload an audio file of your vocals (MP3, WAV, M4A).",
          dropzone: "Drag & drop a file here or click to browse",
          maxSize: "Max file size: {{size}} MB",
          selectedFile: "Selected: {{name}}",
          removeBtn: "Remove",
          formatError: "Unsupported file format. Please use MP3, WAV, or M4A.",
          sizeError: "File is too large. Maximum size is {{size}} MB.",
        },
      },

      // Step 3 — Style
      style: {
        heading: "Pick a Style",
        genre: {
          label: "Genre",
          placeholder: "Select a genre",
          options: {
            pop: "Pop",
            rock: "Rock",
            hiphop: "Hip-Hop",
            rnb: "R&B",
            electronic: "Electronic",
            country: "Country",
            jazz: "Jazz",
            classical: "Classical",
            folk: "Folk",
            reggae: "Reggae",
            latin: "Latin",
            metal: "Metal",
            indie: "Indie",
            blues: "Blues",
            kpop: "K-Pop",
          },
        },
        mood: {
          label: "Mood",
          placeholder: "Select a mood",
          options: {
            happy: "Happy",
            sad: "Sad",
            energetic: "Energetic",
            calm: "Calm",
            romantic: "Romantic",
            angry: "Angry",
            nostalgic: "Nostalgic",
            dreamy: "Dreamy",
            dark: "Dark",
            uplifting: "Uplifting",
            melancholic: "Melancholic",
            playful: "Playful",
          },
        },
        tempo: {
          label: "Tempo",
          slow: "Slow",
          medium: "Medium",
          fast: "Fast",
          bpm: "{{value}} BPM",
        },
        energy: {
          label: "Energy",
          low: "Low",
          medium: "Medium",
          high: "High",
        },
        duration: {
          label: "Duration",
          short: "Short (~1 min)",
          medium: "Medium (~2-3 min)",
          long: "Long (~4-5 min)",
        },
      },

      // Step 4 — Review
      review: {
        heading: "Review Your Song",
        titleLabel: "Title",
        lyricsLabel: "Lyrics",
        voiceLabel: "Voice",
        styleLabel: "Style",
        genreLabel: "Genre",
        moodLabel: "Mood",
        tempoLabel: "Tempo",
        energyLabel: "Energy",
        durationLabel: "Duration",
        editBtn: "Edit",
        confirmMessage:
          "Everything look good? Hit generate to create your song!",
      },
    },

    // -------------------------------------------------------
    // Generation screen
    // -------------------------------------------------------
    generation: {
      title: "Creating Your Song",
      subtitle: "Sit tight — this usually takes about a minute.",
      steps: {
        lyrics: "Writing lyrics...",
        composing: "Composing the melody...",
        vocals: "Rendering vocals...",
        mixing: "Mixing & mastering...",
        finalizing: "Finalizing...",
      },
      tip: "Tip: Great songs sometimes need a second take. You can regenerate if you're not happy with the result.",
      errorTitle: "Generation Failed",
      errorMessage:
        "Something went wrong while creating your song. Please try again.",
      retryBtn: "Try Again",
    },

    // -------------------------------------------------------
    // Song Result page
    // -------------------------------------------------------
    songResult: {
      title: "Your Song Is Ready!",
      nowPlaying: "Now Playing",
      playBtn: "Play",
      pauseBtn: "Pause",
      downloadBtn: "Download",
      shareBtn: "Share",
      regenerateBtn: "Regenerate",
      createVideoBtn: "Create Video",
      newSongBtn: "Create Another Song",
      shareModal: {
        title: "Share Your Song",
        copyLink: "Copy Link",
        copied: "Copied!",
        shareOn: "Share on",
        facebook: "Facebook",
        twitter: "X (Twitter)",
        whatsapp: "WhatsApp",
        email: "Email",
      },
      regenerateModal: {
        title: "Regenerate Song?",
        message:
          "This will create a new version with the same settings. Your current version will still be saved.",
        cancelBtn: "Cancel",
        confirmBtn: "Regenerate",
      },
    },

    // -------------------------------------------------------
    // My Songs Library
    // -------------------------------------------------------
    mySongs: {
      title: "My Songs",
      subtitle: "All your creations in one place",
      empty: {
        title: "No songs yet",
        message: "Create your first song and it will appear here.",
        ctaBtn: "Create a Song",
      },
      songCard: {
        playBtn: "Play",
        pauseBtn: "Pause",
        downloadBtn: "Download",
        shareBtn: "Share",
        deleteBtn: "Delete",
        createdAt: "Created {{date}}",
      },
      deleteModal: {
        title: "Delete Song?",
        message:
          "Are you sure you want to delete \"{{title}}\"? This action cannot be undone.",
        cancelBtn: "Cancel",
        confirmBtn: "Delete",
      },
      sortBy: {
        label: "Sort by",
        newest: "Newest first",
        oldest: "Oldest first",
        title: "Title A-Z",
      },
      searchPlaceholder: "Search songs...",
    },

    // -------------------------------------------------------
    // Video Creator
    // -------------------------------------------------------
    videoCreator: {
      pageTitle: "Create a Video",
      steps: {
        selectSong: "Select Song",
        uploadPhoto: "Upload Photo",
        customize: "Customize",
        preview: "Preview & Export",
      },
      selectSong: {
        heading: "Choose a Song",
        noSongs: "You don't have any songs yet. Create one first!",
        createSongBtn: "Create a Song",
        selectedLabel: "Selected",
      },
      uploadPhoto: {
        heading: "Add a Photo or Image",
        instructions: "This image will be the visual for your video reel.",
        dropzone: "Drag & drop an image here or click to browse",
        supportedFormats: "JPG, PNG, or WebP — max {{size}} MB",
        removeBtn: "Remove",
        cropBtn: "Crop & Adjust",
      },
      customize: {
        heading: "Customize Your Video",
        theme: {
          label: "Theme",
          options: {
            minimal: "Minimal",
            neon: "Neon Glow",
            vintage: "Vintage",
            gradient: "Gradient Wave",
            particles: "Particles",
            waveform: "Waveform",
          },
        },
        textOverlay: {
          label: "Text Overlay",
          placeholder: "e.g. Song title, artist name",
          positionLabel: "Text Position",
          positions: {
            top: "Top",
            center: "Center",
            bottom: "Bottom",
          },
        },
        colorAccent: {
          label: "Accent Color",
        },
        aspectRatio: {
          label: "Aspect Ratio",
          portrait: "Portrait (9:16)",
          landscape: "Landscape (16:9)",
          square: "Square (1:1)",
        },
      },
      preview: {
        heading: "Preview & Export",
        previewBtn: "Play Preview",
        exportBtn: "Export Video",
        exportingMessage: "Exporting your video...",
        downloadBtn: "Download Video",
        shareBtn: "Share Video",
        startOverBtn: "Start Over",
      },
      nav: {
        back: "Back",
        next: "Next",
      },
    },

    // -------------------------------------------------------
    // Common / Shared
    // -------------------------------------------------------
    common: {
      loading: "Loading...",
      error: "Something went wrong",
      retry: "Try Again",
      save: "Save",
      cancel: "Cancel",
      confirm: "Confirm",
      delete: "Delete",
      edit: "Edit",
      close: "Close",
      yes: "Yes",
      no: "No",
      ok: "OK",
      done: "Done",
      or: "or",
      and: "and",
      searchPlaceholder: "Search...",
      noResults: "No results found",
      requiredField: "This field is required",
      back: "Back",
      next: "Next",
      signIn: "Sign in",
      generate: "Generate",
      generating: "Generating...",
      download: "Download",
      downloading: "Downloading...",
      songs: "songs",
      song: "song",
      untitled: "Untitled",
      processing: "Processing...",
      pending: "Pending",
      completed: "Completed",
      failed: "Failed",
    },

    // Homepage extra
    home: {
      createYour: "CREATE YOUR",
      withAi: "W I T H   A I",
      aiPowered: "AI POWERED",
      heroSubtitle: "Write lyrics, choose a style, pick a voice — your song is ready in minutes.",
      tryFree: "Try free",
      startCreating: "Start Creating",
      createReel: "Create Reel",
      promptTitle: "What song do you want to create?",
      promptSubtitle: "Describe your song idea and AI will write the lyrics and generate the music",
      promptPlaceholder: "A romantic ballad about long-distance love, soft piano, female voice, emotional and cinematic...",
      loveSong: "Love song",
      birthdaySong: "Birthday song",
      rapBeat: "Rap beat",
      kidsSong: "Kids song",
      weddingSong: "Wedding song",
      writeOrGenerate: "Write or Generate Lyrics",
      writeOrGenerateDesc: "Type your own, describe what you want, or let AI write it for you.",
      startWriting: "Start writing",
      chooseStyle: "Choose Style & Voice",
      chooseStyleDesc: "Pick genre, mood, male/female voice — or describe the exact sound you want.",
      exploreStyles: "Explore styles",
      createVideoReels: "Create Video Reels",
      createVideoReelsDesc: "Turn your song into a video with synced captions and beautiful templates.",
      makeAReel: "Make a reel",
      recentSongs: "Your Recent Songs",
      viewAll: "View all",
      choosePackage: "Choose Your Package",
      packageSubtitle: "Create personalized songs powered by AI",
      standard: "Standard",
      premium: "Premium",
      express: "Express",
      mostSelected: "Most selected",
      orderNow: "Order now",
      personalizedSong: "1 personalized song",
      personalizedVersions: "2 personalized versions",
      fivePersonalizedSongs: "5 personalized songs",
      audioLyrics: "Audio + lyrics text",
      instantDownload: "Instant MP3 download",
      videoReelLyrics: "Video reel with lyrics",
      uploadImage: "Upload your own image",
      fastProcessing: "Fast processing",
      textCorrection: "1 text correction after delivery",
    },

    // Create page
    create: {
      subtitle: "Everything in one place — write, style, generate.",
      lyrics: "Lyrics",
      write: "Write",
      ai: "AI",
      qa: "Q&A",
      writePlaceholder: "[Verse 1]\nWrite your lyrics here...\n\n[Chorus]\nYour chorus goes here...",
      aiPlaceholder: "A love song about summer nights, nostalgic and dreamy...",
      generateLyrics: "Generate Lyrics",
      whatAbout: "What is the song about?",
      whatEmotion: "What emotion should it convey?",
      anyStory: "Any specific story or details?",
      generateFromAnswers: "Generate from Answers",
      generated: "Generated",
      regenerate: "Regenerate",
      startOver: "Start over",
      editPlaceholder: "Ask AI to edit: make chorus catchier, add a bridge...",
      songTitle: "Song Title",
      aiSuggest: "AI suggest",
      titlePlaceholder: "My Awesome Song",
      voice: "Voice",
      female: "Female",
      male: "Male",
      femaleSub: "Soft, powerful, soulful",
      maleSub: "Deep, smooth, raspy",
      style: "Style",
      genre: "Genre",
      mood: "Mood",
      stylePrompt: "Style Prompt",
      likeSuno: "(like Suno)",
      stylePromptPlaceholder: "e.g. acoustic guitar, soft harmonies, lo-fi warmth, gentle percussion...",
      tempo: "Tempo",
      energy: "Energy",
      generatingYourSong: "Generating your song...",
      generateSong: "Generate Song",
      writeLyricsAndPick: "Write lyrics and pick a genre to generate",
    },

    // Generating page
    generating: {
      writingLyrics: "Writing lyrics",
      composingMelody: "Composing melody",
      renderingVocals: "Rendering vocals",
      applyingVoice: "Applying your voice",
      mixingMastering: "Mixing & mastering",
      title: "Creating Your Song",
      subtitle: "This usually takes 1-3 minutes. Sit back and enjoy the magic!",
      failedTitle: "Generation Failed",
      failedDefault: "Something went wrong while creating your song. Please try again.",
      tryAgain: "Try Again",
    },

    // Song result
    result: {
      completed: "Completed",
      audioNotAvailable: "Audio not available yet",
      createReel: "Create Reel",
      download: "Download",
      share: "Share",
      copied: "Copied!",
      newSong: "New Song",
      library: "Library",
      songDetails: "Song Details",
      lyrics: "Lyrics",
      editLyrics: "Edit lyrics",
      genre: "Genre",
      mood: "Mood",
      tempo: "Tempo",
      energy: "Energy",
    },

    // Video creator
    video: {
      title: "Create Reel Video",
      subtitle: "Turn your song into a shareable video reel",
      steps: { selectSong: "Select Song", uploadPhoto: "Upload Photo", customize: "Customize", preview: "Preview & Export" },
      chooseSong: "Choose a Song",
      noSongs: "No completed songs yet.",
      createSong: "Create Song",
      transcribing: "Transcribing song for auto-captions...",
      captionsSynced: "caption segments auto-synced from audio",
      noCaptions: "No captions detected — check OpenAI key",
      uploadPhoto: "Upload Photo",
      optional: "(optional)",
      clickToUpload: "Click to upload a photo",
      photoFormats: "JPG, PNG — square works best",
      skipPhoto: "Skip — continue without photo",
      chooseTemplate: "Choose Template",
      chooseTheme: "Choose Theme",
      songLabel: "Song:",
      captionsLabel: "Captions:",
      formatLabel: "Format: 1080 x 1920 (Reel)",
      previewExport: "Preview & Export",
      preview: "Preview",
      pause: "Pause",
      rendering: "Rendering...",
      exportMp4: "Export as MP4",
      downloadMp4: "Download MP4",
      reExport: "Re-export",
      renderingOffline: "Rendering video offline — no audio playback needed",
      noPhotoMsg: "No photo? No problem — this layout works without one.",
      clickToChange: "Click to change",
      remove: "Remove",
      watermark: "Made with create-song",
    },

    // Shared song
    shared: {
      notFound: "Song Not Found",
      notFoundMsg: "This shared link may have expired or the song was removed.",
      goHome: "Go Home",
      sharedVia: "Shared via create-song",
      audioNotAvailable: "Audio not available",
      wantToCreate: "Want to create your own AI-generated song?",
      createFree: "Create Your Song Free",
    },

    // Library extra
    lib: {
      reel: "Reel",
    },
  },

  // =======================================================
  //  SLOVENIAN — sl
  // =======================================================
  sl: {
    // -------------------------------------------------------
    // Blagovna znamka
    // -------------------------------------------------------
    brand: {
      name: "Ustvari Pesem",
      tagline: "Tvoja glasba, na tvoj nacin",
    },

    // -------------------------------------------------------
    // Navigacija
    // -------------------------------------------------------
    nav: {
      home: "Domov",
      createSong: "Ustvari pesem",
      mySongs: "Moje pesmi",
      videoCreator: "Ustvari video",
      login: "Prijava",
      register: "Registracija",
      logout: "Odjava",
      profile: "Profil",
      language: "Jezik",
    },

    // -------------------------------------------------------
    // Pristajalna stran
    // -------------------------------------------------------
    landing: {
      hero: {
        title: "Ustvari svojo pesem v nekaj minutah",
        subtitle:
          "Napisi besedilo, izberi glas, doloci slog — in pusti, da jo umetna inteligenca spremeni v pravo pesem.",
        cta: "Zacni ustvarjati",
        ctaSecondary: "Poslušaj primere",
      },
      howItWorks: {
        heading: "Kako deluje",
        stepLabel: "Korak",
        step1: {
          title: "Napisi besedilo",
          description:
            "Napisi lastno besedilo, pusti, da ga ustvari umetna inteligenca, ali odgovori na nekaj vodenih vprasanj.",
        },
        step2: {
          title: "Izberi glas in slog",
          description:
            "Izberi AI glas ali posnemi svojega. Doloci zvrst, razpolozenje, tempo in energijo.",
        },
        step3: {
          title: "Ustvari in deli",
          description:
            "Pritisni ustvari, pocakaj trenutek — in tvoja pesem je pripravljena za predvajanje, prenos in deljenje.",
        },
      },
      examples: {
        heading: "Primeri pesmi",
        listenBtn: "Poslušaj",
        madeWith: "Ustvarjeno z Ustvari Pesem",
      },
      cta: {
        title: "Pripravljen na svojo pesem?",
        subtitle: "Brezplacno — 3 pesmi na mesec, brez kreditne kartice.",
        button: "Zacni brezplacno",
      },
      footer: "© 2025 Ustvari Pesem. Poganja umetna inteligenca.",
    },

    // -------------------------------------------------------
    // Avtentikacija
    // -------------------------------------------------------
    auth: {
      login: {
        title: "Dobrodosli nazaj",
        subtitle: "Prijavi se in nadaljuj z ustvarjanjem glasbe",
        emailLabel: "E-posta",
        emailPlaceholder: "ti@primer.si",
        passwordLabel: "Geslo",
        passwordPlaceholder: "Vnesi svoje geslo",
        submitBtn: "Prijava",
        forgotPassword: "Pozabljeno geslo?",
        noAccount: "Nimas racuna?",
        signUpLink: "Registriraj se",
        orDivider: "ali",
        googleBtn: "Nadaljuj z Googlom",
        errorInvalid: "Napacen e-postni naslov ali geslo.",
        errorGeneric: "Prislo je do napake. Poskusi znova.",
      },
      register: {
        title: "Ustvari svoj racun",
        subtitle: "Zacni ustvarjati pesmi v nekaj minutah",
        nameLabel: "Polno ime",
        namePlaceholder: "Tvoje ime",
        emailLabel: "E-posta",
        emailPlaceholder: "ti@primer.si",
        passwordLabel: "Geslo",
        passwordPlaceholder: "Ustvari geslo",
        confirmPasswordLabel: "Potrdi geslo",
        confirmPasswordPlaceholder: "Ponovno vnesi geslo",
        submitBtn: "Registracija",
        hasAccount: "Ze imas racun?",
        loginLink: "Prijavi se",
        orDivider: "ali",
        googleBtn: "Registriraj se z Googlom",
        errorPasswordMismatch: "Gesli se ne ujemata.",
        errorEmailTaken: "Racun s tem e-postnim naslovom ze obstaja.",
        errorGeneric: "Prislo je do napake. Poskusi znova.",
        termsNotice: "Z registracijo se strinjas z nasimi",
        termsLink: "Pogoji uporabe",
        andWord: "in",
        privacyLink: "Politika zasebnosti",
      },
      forgotPassword: {
        title: "Ponastavi geslo",
        subtitle: "Na tvoj e-postni naslov bomo poslali povezavo za ponastavitev",
        emailLabel: "E-posta",
        emailPlaceholder: "ti@primer.si",
        submitBtn: "Poslji povezavo",
        backToLogin: "Nazaj na prijavo",
        successMessage: "Preveri svoj e-postni nabiralnik za povezavo za ponastavitev.",
      },
    },

    // -------------------------------------------------------
    // Ustvarjalec pesmi (4 koraki)
    // -------------------------------------------------------
    songCreator: {
      pageTitle: "Ustvari pesem",
      stepper: {
        lyrics: "Besedilo",
        voice: "Glas",
        style: "Slog",
        review: "Pregled",
      },
      nav: {
        back: "Nazaj",
        next: "Naprej",
        generate: "Ustvari pesem",
      },

      // Korak 1 — Besedilo
      lyrics: {
        heading: "Napisi besedilo",
        tabs: {
          write: "Napisi sam/a",
          ai: "AI iz opisa",
          guided: "Vodena vprasanja",
        },
        write: {
          placeholder: "Vnesi ali prilepi besedilo tukaj...",
          charCount: "{{count}} znakov",
        },
        ai: {
          label: "Opisi pesem, ki jo zelis",
          placeholder:
            "npr. Vesela pop pesem o poletnem izletu s prijatelji",
          generateBtn: "Ustvari besedilo",
          regenerateBtn: "Poskusi znova",
          loading: "Pisem besedilo...",
          acceptBtn: "Uporabi to besedilo",
          editBtn: "Uredi pred uporabo",
        },
        guided: {
          q1: "O cem govori pesem?",
          q1Placeholder: "npr. Selitev v novo mesto",
          q2: "Kaksen obcutek naj ima?",
          q2Placeholder: "npr. Poln upanja, a malo nervozen",
          q3: "Ali zelis vkljuciti dolocene besede ali fraze?",
          q3Placeholder: "npr. novi zacetki, odprta cesta",
          q4: "Komu je pesem namenjena?",
          q4Placeholder: "npr. Sebi, prijatelju, partnerju",
          generateBtn: "Ustvari besedilo iz odgovorov",
          loading: "Ustvarjam besedilo...",
        },
        songTitle: {
          label: "Naslov pesmi",
          placeholder: "Poimenuj svojo pesem",
        },
        validation: {
          tooShort: "Besedilo je prekratko. Napisi vsaj nekaj vrstic.",
          titleRequired: "Prosim vnesi naslov pesmi.",
        },
      },

      // Korak 2 — Glas
      voice: {
        heading: "Izberi glas",
        tabs: {
          ai: "AI glas",
          record: "Posnemi svoj glas",
          upload: "Nalozi posnetek",
        },
        ai: {
          label: "Izberi AI glas",
          previewBtn: "Predogled",
          male: "Moski",
          female: "Zenski",
          neutral: "Nevtralen",
        },
        record: {
          instructions: "Pritisni gumb in zapoj ali izgovori svoje besedilo.",
          startBtn: "Zacni snemanje",
          stopBtn: "Ustavi snemanje",
          recordingStatus: "Snemanje...",
          playbackBtn: "Predvajaj",
          retryBtn: "Posnemi znova",
          useBtn: "Uporabi ta posnetek",
          permissionError:
            "Potreben je dostop do mikrofona. Dovoli ga v nastavitvah brskalnika.",
        },
        upload: {
          instructions: "Nalozi zvocno datoteko s svojim vokalom (MP3, WAV, M4A).",
          dropzone: "Povleci datoteko sem ali klikni za izbiro",
          maxSize: "Najvecja velikost datoteke: {{size}} MB",
          selectedFile: "Izbrano: {{name}}",
          removeBtn: "Odstrani",
          formatError:
            "Nepodprta oblika datoteke. Uporabi MP3, WAV ali M4A.",
          sizeError: "Datoteka je prevelika. Najvecja velikost je {{size}} MB.",
        },
      },

      // Korak 3 — Slog
      style: {
        heading: "Izberi slog",
        genre: {
          label: "Zvrst",
          placeholder: "Izberi zvrst",
          options: {
            pop: "Pop",
            rock: "Rock",
            hiphop: "Hip-Hop",
            rnb: "R&B",
            electronic: "Elektronska",
            country: "Country",
            jazz: "Jazz",
            classical: "Klasicna",
            folk: "Folk",
            reggae: "Reggae",
            latin: "Latinska",
            metal: "Metal",
            indie: "Indie",
            blues: "Blues",
            kpop: "K-Pop",
          },
        },
        mood: {
          label: "Razpolozenje",
          placeholder: "Izberi razpolozenje",
          options: {
            happy: "Veselo",
            sad: "Zalostno",
            energetic: "Energicno",
            calm: "Mirno",
            romantic: "Romanticno",
            angry: "Jezno",
            nostalgic: "Nostalgicno",
            dreamy: "Sanjavo",
            dark: "Temno",
            uplifting: "Navdihujuce",
            melancholic: "Melanholicno",
            playful: "Razigrano",
          },
        },
        tempo: {
          label: "Tempo",
          slow: "Pocasen",
          medium: "Srednji",
          fast: "Hiter",
          bpm: "{{value}} BPM",
        },
        energy: {
          label: "Energija",
          low: "Nizka",
          medium: "Srednja",
          high: "Visoka",
        },
        duration: {
          label: "Trajanje",
          short: "Kratko (~1 min)",
          medium: "Srednje (~2-3 min)",
          long: "Dolgo (~4-5 min)",
        },
      },

      // Korak 4 — Pregled
      review: {
        heading: "Preglej svojo pesem",
        titleLabel: "Naslov",
        lyricsLabel: "Besedilo",
        voiceLabel: "Glas",
        styleLabel: "Slog",
        genreLabel: "Zvrst",
        moodLabel: "Razpolozenje",
        tempoLabel: "Tempo",
        energyLabel: "Energija",
        durationLabel: "Trajanje",
        editBtn: "Uredi",
        confirmMessage:
          "Vse je v redu? Pritisni ustvari in ustvari svojo pesem!",
      },
    },

    // -------------------------------------------------------
    // Zaslon generiranja
    // -------------------------------------------------------
    generation: {
      title: "Ustvarjam tvojo pesem",
      subtitle: "Malo potrpljenja — obicajno traja priblizno minuto.",
      steps: {
        lyrics: "Pisem besedilo...",
        composing: "Skladam melodijo...",
        vocals: "Ustvarjam vokal...",
        mixing: "Mikšam in mastiram...",
        finalizing: "Zakljucujem...",
      },
      tip: "Namig: Odlicne pesmi vcasih potrebujejo se en poskus. Ce nisi zadovoljen z rezultatom, lahko pesem ustvaris znova.",
      errorTitle: "Ustvarjanje ni uspelo",
      errorMessage:
        "Med ustvarjanjem pesmi je prislo do napake. Prosim poskusi znova.",
      retryBtn: "Poskusi znova",
    },

    // -------------------------------------------------------
    // Stran z rezultatom pesmi
    // -------------------------------------------------------
    songResult: {
      title: "Tvoja pesem je pripravljena!",
      nowPlaying: "Predvaja se",
      playBtn: "Predvajaj",
      pauseBtn: "Premor",
      downloadBtn: "Prenesi",
      shareBtn: "Deli",
      regenerateBtn: "Ustvari znova",
      createVideoBtn: "Ustvari video",
      newSongBtn: "Ustvari novo pesem",
      shareModal: {
        title: "Deli svojo pesem",
        copyLink: "Kopiraj povezavo",
        copied: "Kopirano!",
        shareOn: "Deli na",
        facebook: "Facebook",
        twitter: "X (Twitter)",
        whatsapp: "WhatsApp",
        email: "E-posta",
      },
      regenerateModal: {
        title: "Ustvari pesem znova?",
        message:
          "To bo ustvarilo novo razlicico z enakimi nastavitvami. Tvoja trenutna razlicica bo se vedno shranjena.",
        cancelBtn: "Preklici",
        confirmBtn: "Ustvari znova",
      },
    },

    // -------------------------------------------------------
    // Knjiznica mojih pesmi
    // -------------------------------------------------------
    mySongs: {
      title: "Moje pesmi",
      subtitle: "Vse tvoje stvaritve na enem mestu",
      empty: {
        title: "Se ni pesmi",
        message: "Ustvari svojo prvo pesem in pojavila se bo tukaj.",
        ctaBtn: "Ustvari pesem",
      },
      songCard: {
        playBtn: "Predvajaj",
        pauseBtn: "Premor",
        downloadBtn: "Prenesi",
        shareBtn: "Deli",
        deleteBtn: "Izbrisi",
        createdAt: "Ustvarjeno {{date}}",
      },
      deleteModal: {
        title: "Izbrisi pesem?",
        message:
          "Ali res zelis izbrisati \"{{title}}\"? Tega dejanja ni mogoce razveljaviti.",
        cancelBtn: "Preklici",
        confirmBtn: "Izbrisi",
      },
      sortBy: {
        label: "Razvrsti po",
        newest: "Najnovejse najprej",
        oldest: "Najstarejse najprej",
        title: "Naslov A-Z",
      },
      searchPlaceholder: "Iskanje pesmi...",
    },

    // -------------------------------------------------------
    // Ustvarjalec videa
    // -------------------------------------------------------
    videoCreator: {
      pageTitle: "Ustvari video",
      steps: {
        selectSong: "Izberi pesem",
        uploadPhoto: "Nalozi fotografijo",
        customize: "Prilagodi",
        preview: "Predogled in izvoz",
      },
      selectSong: {
        heading: "Izberi pesem",
        noSongs: "Se nimas nobene pesmi. Najprej ustvari eno!",
        createSongBtn: "Ustvari pesem",
        selectedLabel: "Izbrano",
      },
      uploadPhoto: {
        heading: "Dodaj fotografijo ali sliko",
        instructions: "Ta slika bo vizualna podlaga za tvoj video.",
        dropzone: "Povleci sliko sem ali klikni za izbiro",
        supportedFormats: "JPG, PNG ali WebP — najvec {{size}} MB",
        removeBtn: "Odstrani",
        cropBtn: "Obrezi in prilagodi",
      },
      customize: {
        heading: "Prilagodi svoj video",
        theme: {
          label: "Tema",
          options: {
            minimal: "Minimalisticna",
            neon: "Neonski sij",
            vintage: "Vintage",
            gradient: "Gradientni val",
            particles: "Delci",
            waveform: "Zvocni val",
          },
        },
        textOverlay: {
          label: "Besedilo na videu",
          placeholder: "npr. Naslov pesmi, ime izvajalca",
          positionLabel: "Polozaj besedila",
          positions: {
            top: "Zgoraj",
            center: "Na sredini",
            bottom: "Spodaj",
          },
        },
        colorAccent: {
          label: "Poudarjena barva",
        },
        aspectRatio: {
          label: "Razmerje stranic",
          portrait: "Pokoncno (9:16)",
          landscape: "Lezece (16:9)",
          square: "Kvadratno (1:1)",
        },
      },
      preview: {
        heading: "Predogled in izvoz",
        previewBtn: "Predvajaj predogled",
        exportBtn: "Izvozi video",
        exportingMessage: "Izvazam tvoj video...",
        downloadBtn: "Prenesi video",
        shareBtn: "Deli video",
        startOverBtn: "Zacni znova",
      },
      nav: {
        back: "Nazaj",
        next: "Naprej",
      },
    },

    // -------------------------------------------------------
    // Skupno
    // -------------------------------------------------------
    common: {
      loading: "Nalaganje...",
      error: "Prislo je do napake",
      retry: "Poskusi znova",
      save: "Shrani",
      cancel: "Preklici",
      confirm: "Potrdi",
      delete: "Izbrisi",
      edit: "Uredi",
      close: "Zapri",
      yes: "Da",
      no: "Ne",
      ok: "V redu",
      done: "Koncano",
      or: "ali",
      and: "in",
      searchPlaceholder: "Iskanje...",
      noResults: "Ni zadetkov",
      requiredField: "To polje je obvezno",
      back: "Nazaj",
      next: "Naprej",
      signIn: "Prijava",
      generate: "Ustvari",
      generating: "Ustvarjam...",
      download: "Prenesi",
      downloading: "Prenasam...",
      songs: "pesmi",
      song: "pesem",
      untitled: "Brez naslova",
      processing: "V obdelavi...",
      pending: "Cakanje",
      completed: "Koncano",
      failed: "Neuspesno",
    },

    home: {
      createYour: "USTVARI SVOJO",
      withAi: "Z   U I",
      aiPowered: "UMETNA INTELIGENCA",
      heroSubtitle: "Napisi besedilo, izberi slog, izberi glas — tvoja pesem je pripravljena v minutah.",
      tryFree: "Preizkusi brezplacno",
      startCreating: "Zacni ustvarjati",
      createReel: "Ustvari reel",
      promptTitle: "Kaksen pesem zelis ustvariti?",
      promptSubtitle: "Opisi svojo idejo za pesem in UI bo napisal besedilo ter ustvaril glasbo",
      promptPlaceholder: "Romanticna balada o ljubezni na daljavo, nezen klavir, zenski glas, custvena in filmska...",
      loveSong: "Ljubezenska pesem",
      birthdaySong: "Rojstnodnevna pesem",
      rapBeat: "Rap ritem",
      kidsSong: "Otraska pesem",
      weddingSong: "Porocna pesem",
      writeOrGenerate: "Napisi ali ustvari besedilo",
      writeOrGenerateDesc: "Napisi sam, opisi kaj zelis, ali pusti UI da napise za tebe.",
      startWriting: "Zacni pisati",
      chooseStyle: "Izberi slog in glas",
      chooseStyleDesc: "Izberi zvrst, razpolozenje, moski/zenski glas — ali opisi natancen zvok.",
      exploreStyles: "Razisci sloge",
      createVideoReels: "Ustvari video reele",
      createVideoReelsDesc: "Spremeni pesem v video s sinhroniziranimi podnapisi in lepimi predlogami.",
      makeAReel: "Ustvari reel",
      recentSongs: "Tvoje zadnje pesmi",
      viewAll: "Poglej vse",
      choosePackage: "Izberi svoj paket",
      packageSubtitle: "Ustvari personalizirane pesmi z umetno inteligenco",
      standard: "Standardni",
      premium: "Premium",
      express: "Ekspresni",
      mostSelected: "Najbolj priljubljen",
      orderNow: "Naroci zdaj",
      personalizedSong: "1 personalizirana pesem",
      personalizedVersions: "2 personalizirani razlicici",
      fivePersonalizedSongs: "5 personaliziranih pesmi",
      audioLyrics: "Zvok + besedilo",
      instantDownload: "Takojsnji MP3 prenos",
      videoReelLyrics: "Video reel z besedilom",
      uploadImage: "Nalozi svojo sliko",
      fastProcessing: "Hitra obdelava",
      textCorrection: "1 popravek besedila po dostavi",
    },

    create: {
      subtitle: "Vse na enem mestu — napisi, oblikuj, ustvari.",
      lyrics: "Besedilo",
      write: "Napisi",
      ai: "UI",
      qa: "V&O",
      writePlaceholder: "[Kitica 1]\nNapisi besedilo tukaj...\n\n[Refren]\nTvoj refren gre sem...",
      aiPlaceholder: "Ljubezenska pesem o poletnih noceh, nostalgicna in sanjava...",
      generateLyrics: "Ustvari besedilo",
      whatAbout: "O cem govori pesem?",
      whatEmotion: "Kaksen obcutek naj izraza?",
      anyStory: "Kaksna posebna zgodba ali podrobnosti?",
      generateFromAnswers: "Ustvari iz odgovorov",
      generated: "Ustvarjeno",
      regenerate: "Ustvari znova",
      startOver: "Zacni znova",
      editPlaceholder: "Prosi UI za spremembo: naredi refren bolj catchy, dodaj bridge...",
      songTitle: "Naslov pesmi",
      aiSuggest: "UI predlog",
      titlePlaceholder: "Moja super pesem",
      voice: "Glas",
      female: "Zenski",
      male: "Moski",
      femaleSub: "Nezen, mocen, custven",
      maleSub: "Globok, gladek, hrapav",
      style: "Slog",
      genre: "Zvrst",
      mood: "Razpolozenje",
      stylePrompt: "Slogovni opis",
      likeSuno: "(kot Suno)",
      stylePromptPlaceholder: "npr. akusticna kitara, nezne harmonije, lo-fi toplota, nezna bobnarija...",
      tempo: "Tempo",
      energy: "Energija",
      generatingYourSong: "Ustvarjam tvojo pesem...",
      generateSong: "Ustvari pesem",
      writeLyricsAndPick: "Napisi besedilo in izberi zvrst za ustvarjanje",
    },

    generating: {
      writingLyrics: "Pisem besedilo",
      composingMelody: "Skladam melodijo",
      renderingVocals: "Ustvarjam vokal",
      applyingVoice: "Dodajam tvoj glas",
      mixingMastering: "Miksam in mastiram",
      title: "Ustvarjam tvojo pesem",
      subtitle: "Obicajno traja 1-3 minute. Uzivaj v caranju!",
      failedTitle: "Ustvarjanje ni uspelo",
      failedDefault: "Prislo je do napake pri ustvarjanju pesmi. Poskusi znova.",
      tryAgain: "Poskusi znova",
    },

    result: {
      completed: "Koncano",
      audioNotAvailable: "Zvok se ni na voljo",
      createReel: "Ustvari reel",
      download: "Prenesi",
      share: "Deli",
      copied: "Kopirano!",
      newSong: "Nova pesem",
      library: "Knjiznica",
      songDetails: "Podrobnosti pesmi",
      lyrics: "Besedilo",
      editLyrics: "Uredi besedilo",
      genre: "Zvrst",
      mood: "Razpolozenje",
      tempo: "Tempo",
      energy: "Energija",
    },

    video: {
      title: "Ustvari reel video",
      subtitle: "Spremeni pesem v deljiv video reel",
      steps: { selectSong: "Izberi pesem", uploadPhoto: "Nalozi fotografijo", customize: "Prilagodi", preview: "Predogled in izvoz" },
      chooseSong: "Izberi pesem",
      noSongs: "Se nimas koncanih pesmi.",
      createSong: "Ustvari pesem",
      transcribing: "Prepis pesmi za samodejne podnapise...",
      captionsSynced: "segmentov podnapisov samodejno sinhroniziranih",
      noCaptions: "Podnapisi niso zaznani — preveri OpenAI kljuc",
      uploadPhoto: "Nalozi fotografijo",
      optional: "(neobvezno)",
      clickToUpload: "Klikni za nalaganje fotografije",
      photoFormats: "JPG, PNG — kvadratne najbolje delujejo",
      skipPhoto: "Preskoci — nadaljuj brez fotografije",
      chooseTemplate: "Izberi predlogo",
      chooseTheme: "Izberi temo",
      songLabel: "Pesem:",
      captionsLabel: "Podnapisi:",
      formatLabel: "Format: 1080 x 1920 (Reel)",
      previewExport: "Predogled in izvoz",
      preview: "Predogled",
      pause: "Premor",
      rendering: "Upodabljam...",
      exportMp4: "Izvozi kot MP4",
      downloadMp4: "Prenesi MP4",
      reExport: "Izvozi znova",
      renderingOffline: "Upodabljanje videa brez predvajanja zvoka",
      noPhotoMsg: "Brez fotografije? Ni problema — ta postavitev deluje brez nje.",
      clickToChange: "Klikni za spremembo",
      remove: "Odstrani",
      watermark: "Ustvarjeno z ustvari-pesem",
    },

    shared: {
      notFound: "Pesem ni najdena",
      notFoundMsg: "Ta deljena povezava je morda potekla ali je bila pesem odstranjena.",
      goHome: "Na zacetno stran",
      sharedVia: "Deljeno prek ustvari-pesem",
      audioNotAvailable: "Zvok ni na voljo",
      wantToCreate: "Zelis ustvariti svojo pesem z umetno inteligenco?",
      createFree: "Ustvari svojo pesem brezplacno",
    },

    lib: {
      reel: "Reel",
    },
  },
  // Croatian — minimal keys (falls back to English via LanguageContext)
  hr: {
    brand: {
      name: "Stvori Pjesmu",
      tagline: "Vaša glazba, vaš način",
    },
  },
};

export default translations;
